<template>
  <div
    class="birthdate-input"
    :class="{ 'birthdate-input--corrected': corrected }"
    @focusin="dismiss_correction"
    @input="user_edited = true"
    @change="user_edited = true"
  >
    <label class="sr-only" :for="input_ids.day">Giorno</label>
    <vb-input
      v-model="birth_day"
      :vb_options="{
        id: input_ids.day,
        autocomplete: 'bday-day',
        max: days_in_selected_month,
        min: 1,
        number: true,
        placeholder: 'Giorno',
        type: 'number',
        value: birth_day,
      }"
    />

    <label class="sr-only" :for="input_ids.month">Mese</label>
    <vb-select
      v-model="birth_month"
      class="birthdate-input__month"
      :class="{ 'birthdate-input__month--placeholder': !month_is_selected }"
      :vb_options="{
        id: input_ids.month,
        options: month_options,
        text_field: 'label',
        value: birth_month,
        value_field: 'index',
      }"
    />

    <label class="sr-only" :for="input_ids.year">Anno</label>
    <vb-input
      v-model="birth_year"
      :vb_options="{
        id: input_ids.year,
        autocomplete: 'bday-year',
        max: selectable_years.max,
        min: selectable_years.min,
        number: true,
        placeholder: 'Anno',
        type: 'number',
        value: birth_year,
      }"
    />
  </div>
</template>

<script>
  import moment from 'moment';
  import {
    build_store_key,
    get_stored_value,
    set_stored_value,
  } from '@/utilities/persistent-inputs';
  import {
    clamp_day,
    clamp_to_bounds,
    compose_birthdate,
    days_in_month,
    parse_birthdate,
    to_integer,
    year_bounds,
  } from '@/utilities/birthdate';

  export default {
    name: 'birthdate-input',
    props: {
      // 'YYYY-MM-DD'; min/max opzionali, tipicamente da get_birth_date_constraints()
      value: { type: String, default: '' },
      min: { type: String, default: '' },
      max: { type: String, default: '' },
      // Base dei tre id di campo (es. build_input_id(traveler, 'birth'))
      input_id: { type: String, default: '' },
      // Persiste la data composta, non le tre parti: restaurarle separatamente
      // potrebbe rimontare una tripla inesistente (31 + febbraio)
      persistent_key: { type: String, default: '' },
      // Bordo di attenzione sui tre campi: la data e' stata sostituita e va
      // ricontrollata. Non e' lo `state` di bootstrap, che ha solo valido /
      // non valido - qui il valore e' valido, solo non quello digitato.
      corrected: { type: Boolean, default: false },
    },
    data() {
      return {
        birth_day: null,
        birth_month: null,
        birth_year: null,
        last_emitted_value: null,
        save_timeout: 0,
        correction: null,
        // Distingue una data digitata da una ripescata: allo mount i bound
        // possono snappare un valore salvato senza che l'utente tocchi nulla.
        user_edited: false,
      };
    },
    computed: {
      input_ids() {
        const base = this.input_id || `birthdate-${this._uid}`;
        return {
          day: `${base}:day`,
          month: `${base}:month`,
          year: `${base}:year`,
        };
      },
      storage_key() {
        if (!this.persistent_key) {
          return '';
        }
        const { name = '' } = this.$route || {};
        return build_store_key(name, this.persistent_key);
      },
      month_options() {
        return [{ index: null, label: 'Mese', disabled: true }].concat(
          moment.months().map((label, index) => ({ index, label }))
        );
      },
      month_is_selected() {
        return to_integer(this.birth_month) !== null;
      },
      days_in_selected_month() {
        return days_in_month(this.birth_year, this.birth_month);
      },
      selectable_years() {
        return year_bounds(this.min, this.max);
      },
      raw_value() {
        return compose_birthdate({
          day: this.birth_day,
          month: this.birth_month,
          year: this.birth_year,
        });
      },
      composed_value() {
        return clamp_to_bounds(this.raw_value, this.min, this.max);
      },
      normalized_value() {
        return this.value || null;
      },
    },
    watch: {
      value: 'sync_from_value',
      birth_day: 'reconcile',
      birth_month: 'reconcile',
      birth_year: 'reconcile',
      // I bound si spostano con la ricerca: una data fuori range va snappata
      min: 'reconcile',
      max: 'reconcile',
    },
    created() {
      this.last_emitted_value = this.normalized_value;
      this.hydrate(this.initial_value());
    },
    methods: {
      // Stesse precedenze di <vb-input>: un `value` in ingresso vince sullo
      // storage e lo riallinea, altrimenti si ripesca quanto salvato.
      initial_value() {
        if (!this.storage_key) {
          return this.value;
        }
        if (this.normalized_value) {
          set_stored_value(this.storage_key, this.normalized_value);
          return this.value;
        }
        return get_stored_value(this.storage_key) || this.value;
      },
      persist(value) {
        if (!this.storage_key) {
          return;
        }
        clearTimeout(this.save_timeout);
        this.save_timeout = setTimeout(
          () => set_stored_value(this.storage_key, value),
          350
        );
      },
      // Chi mette le mani sul campo ha visto la situazione: l'avviso ha finito
      // il suo lavoro e non deve restare li' a segnalare una data ormai vista.
      dismiss_correction() {
        if (this.correction) {
          this.publish_correction(null);
        }
      },
      publish_correction(correction) {
        this.correction = correction;
        if (!correction) {
          this.$emit('corrected', null);
          return;
        }
        this.$emit('corrected', { ...correction, user_edited: this.user_edited });
      },
      hydrate(value) {
        const { day, month, year } = parse_birthdate(value);
        this.birth_day = day;
        this.birth_month = month;
        this.birth_year = year;
      },
      // Un valore in arrivo pari a quanto già pubblicato è la nostra eco:
      // ri-idratare azzererebbe una data ancora parziale.
      sync_from_value() {
        if (this.normalized_value === this.composed_value) {
          return;
        }
        this.last_emitted_value = this.normalized_value;
        this.hydrate(this.value);
      },
      // Ogni passo riscrive una parte ed esce; il watcher rientra col valore
      // già a posto (to_integer, clamp_day e clamp_to_bounds sono idempotenti).
      reconcile() {
        const year = to_integer(this.birth_year);
        if (year !== this.birth_year) {
          this.birth_year = year;
          return;
        }
        const day = clamp_day(
          this.birth_day,
          this.birth_year,
          this.birth_month
        );
        if (day !== this.birth_day) {
          this.birth_day = day;
          return;
        }
        // Snap: i campi devono mostrare esattamente ciò che si pubblica
        if (this.composed_value !== this.raw_value) {
          this.publish_correction({
            from: this.raw_value,
            to: this.composed_value,
          });
          this.hydrate(this.composed_value);
          return;
        }
        // La correzione descrive i campi solo finché mostrano il valore
        // snappato: al primo ritocco dell'utente smette di valere.
        if (this.correction && this.raw_value !== this.correction.to) {
          this.publish_correction(null);
        }
        if (this.composed_value === this.last_emitted_value) {
          return;
        }
        this.last_emitted_value = this.composed_value;
        this.persist(this.composed_value);
        this.$emit('input', this.composed_value);
        this.$emit('update', this.composed_value);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .birthdate-input {
    display: grid;
    grid-template-columns: auto 50% auto;
    gap: $grid-gutter-width / 2;

    &--corrected {
      ::v-deep {
        .form-control,
        .custom-select {
          border-color: $warning;
        }
      }
    }

    // <select> non ha ::placeholder: il grigio va sul controllo e le option
    // vere se lo riprendono in lista. Il segnaposto e' l'unica disabled,
    // quindi resta grigio anche da select chiusa.
    &__month--placeholder {
      color: $input-placeholder-color;

      ::v-deep {
        option:enabled {
          color: $input-color;
        }
      }
    }
  }
</style>
